import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentType } from 'preact';
import { Play, Trash2 } from 'lucide-preact';
import type { CoasterRuntime, RuntimeMediaState } from '../app/runtime-types.ts';
import type { MediaArtifactKind } from '../media/index.ts';
import { CloseButton, Disclosure, OverflowButton } from './primitives.tsx';
import { UI_COPY } from './ui-copy.ts';

type MediaTab = 'photo' | 'highlight' | 'card' | 'loop';

function tabArtifact(tab: MediaTab): MediaArtifactKind {
  if (tab === 'highlight') return 'highlight_video';
  if (tab === 'card') return 'share_card';
  if (tab === 'loop') return 'animated_loop';
  return 'photo';
}

export function MediaStudio({
  state,
  runtime,
  title,
  ReactionOptions,
}: {
  readonly state: RuntimeMediaState;
  readonly runtime: CoasterRuntime;
  readonly title: string;
  readonly ReactionOptions?: ComponentType<{
    state: RuntimeMediaState;
    runtime: CoasterRuntime;
    busy: boolean;
    compact?: boolean;
  }>;
}) {
  const dialogRef = useRef<HTMLElement | null>(null);
  const [tab, setTab] = useState<MediaTab>(() =>
    state.videoUrl ? 'highlight' : state.loopUrl ? 'loop' : 'photo',
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const artifact = tabArtifact(tab);
  const artifactReady =
    tab === 'photo'
      ? Boolean(state.photoUrl)
      : tab === 'card'
        ? Boolean(state.cardUrl)
        : tab === 'highlight'
          ? Boolean(state.videoUrl)
          : Boolean(state.loopUrl);
  const busy = state.status === 'preparing' || state.status === 'encoding';
  const estimatedSeconds = Math.max(1, Math.ceil((1 - state.progress) * (tab === 'loop' ? 5 : 18)));
  const selectedEditDescription =
    state.selectedAlternative === 0
      ? 'Three diverse action moments in ride order.'
      : state.selectedAlternative === 1
        ? 'Drops, airtime, and speed take priority, with at least one inversion when available.'
        : 'The Best Moments sequence from the same first-person view used while riding.';

  useEffect(() => {
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    dialog?.querySelector<HTMLElement>('button, [href], input, select, textarea')?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        runtime.closeMediaStudio();
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
  }, [runtime]);

  return (
    <div class="modal-backdrop media-studio-backdrop" role="presentation">
      <section
        ref={dialogRef}
        class="media-studio"
        role="dialog"
        aria-modal="true"
        aria-labelledby="media-studio-title"
        data-testid="media-studio"
      >
        <header class="media-studio-header">
          <div>
            <h2 id="media-studio-title">{UI_COPY.media.title}</h2>
            <span class="media-studio-coaster-name">{title}</span>
          </div>
          <CloseButton label="Close Share Your Ride" onClick={() => runtime.closeMediaStudio()} />
        </header>

        <div class="media-tabs" role="tablist" aria-label="Share format">
          {(
            [
              ['photo', UI_COPY.media.photo],
              ['highlight', state.videoUrl ? 'Video · Ready' : 'Video'],
              ['card', UI_COPY.media.previewImage],
              ['loop', state.loopUrl ? 'Short Loop · Ready' : UI_COPY.media.shortLoop],
            ] as const
          ).map(([value, label]) => (
            <button
              type="button"
              role="tab"
              aria-selected={tab === value}
              class={tab === value ? 'active' : ''}
              onClick={() => setTab(value)}
            >
              {label}
            </button>
          ))}
        </div>

        <div class="media-preview" data-testid={`media-preview-${tab}`}>
          {tab === 'photo' && state.photoUrl && (
            <img src={state.photoUrl} alt={`Photo from ${title}`} />
          )}
          {tab === 'card' && state.cardUrl && (
            <img src={state.cardUrl} alt={`Share card for ${title}`} />
          )}
          {tab === 'highlight' && state.videoUrl && (
            <video src={state.videoUrl} controls playsInline preload="metadata" />
          )}
          {tab === 'loop' && state.loopUrl && (
            <video src={state.loopUrl} controls playsInline loop muted preload="metadata" />
          )}
          {!artifactReady && (
            <div class="media-placeholder">
              <strong>
                {busy
                  ? 'Working on it…'
                  : tab === 'highlight'
                    ? 'Ready to make your video'
                    : tab === 'loop'
                      ? 'Ready to make a loop'
                      : tab === 'photo'
                        ? 'Preparing Photo'
                        : 'Preparing preview'}
              </strong>
              <span>
                {tab === 'highlight'
                  ? 'A 15-second vertical replay with your best moments.'
                  : tab === 'loop'
                    ? 'A silent three-second looping clip.'
                    : tab === 'photo'
                      ? 'A portrait action image for social feeds.'
                      : 'A landscape overview for links and listings.'}
              </span>
            </div>
          )}
        </div>

        {(tab === 'photo' || tab === 'card') && (
          <p class="media-artifact-purpose">
            {tab === 'photo'
              ? 'Portrait action photo for social posts.'
              : 'Landscape preview shown in coaster listings.'}
          </p>
        )}

        <div class="media-status" aria-live="polite">
          <span>{state.message}</span>
          {state.status === 'encoding' && (
            <>
              <progress max="1" value={state.progress} aria-label="Video generation progress" />
              <small>About {estimatedSeconds} seconds remaining on this device.</small>
            </>
          )}
          {state.timelineSource === 'restored' && (
            <small>Restored from your last completed ride.</small>
          )}
        </div>

        {tab === 'photo' && state.posterCandidates.length > 0 && (
          <div class="poster-choices" role="group" aria-label="Photo moment">
            {state.posterCandidates.map((candidate) => (
              <button
                type="button"
                class={state.selectedPosterId === candidate.id ? 'active' : ''}
                aria-pressed={state.selectedPosterId === candidate.id}
                disabled={busy}
                onClick={() => void runtime.selectPosterCandidate(candidate.id)}
              >
                {candidate.thumbnailUrl ? (
                  <img src={candidate.thumbnailUrl} alt="" aria-hidden="true" />
                ) : (
                  <span class="poster-thumb-placeholder" aria-hidden="true" />
                )}
                <span>{candidate.label}</span>
              </button>
            ))}
            {state.creatorPosterSkipped && (
              <small class="highlight-choice-description media-poster-note">
                The creator marker was too slow or too close to the station, so a stronger action
                moment was selected.
              </small>
            )}
          </div>
        )}

        {tab === 'highlight' && (
          <div class="media-options">
            <div class="highlight-choices" role="group" aria-label="Highlight edit">
              {([0, 1, 2] as const).map((value) => (
                <button
                  type="button"
                  aria-pressed={state.selectedAlternative === value}
                  onClick={() => runtime.selectHighlightAlternative(value)}
                  disabled={busy}
                >
                  {state.editLabels[value]}
                </button>
              ))}
            </div>
            <small class="highlight-choice-description">
              {selectedEditDescription} Generated versions stay ready while this coaster remains
              open.
            </small>
            <label class="toggle-row">
              <input
                type="checkbox"
                checked={state.includeAudio}
                onChange={(event) => runtime.setMediaAudioEnabled(event.currentTarget.checked)}
                disabled={busy}
              />
              <span>Include ride sound</span>
            </label>
            {state.reactionRawUrl && ReactionOptions && (
              <ReactionOptions state={state} runtime={runtime} busy={busy} />
            )}
          </div>
        )}

        {tab === 'loop' && state.reactionRawUrl && ReactionOptions && (
          <ReactionOptions state={state} runtime={runtime} busy={busy} compact />
        )}

        <div class="media-actions">
          {tab === 'highlight' && !state.videoUrl && (
            <button
              class="primary-action"
              type="button"
              disabled={busy || !state.formatLabel || !state.videoAvailable}
              onClick={() => void runtime.generateHighlightVideo()}
            >
              Generate Video{state.formatLabel ? ` (${state.formatLabel})` : ''}
            </button>
          )}
          {tab === 'loop' && !state.loopUrl && (
            <button
              class="primary-action"
              type="button"
              disabled={busy || !state.formatLabel || !state.videoAvailable}
              onClick={() => void runtime.generateAnimatedLoop()}
            >
              Generate Loop
            </button>
          )}
          {state.status === 'encoding' && (
            <button
              class="secondary-action"
              type="button"
              onClick={() => runtime.cancelMediaExport()}
            >
              Cancel
            </button>
          )}
          {artifactReady && (
            <>
              <button
                class="primary-action"
                type="button"
                onClick={() => runtime.downloadMedia(artifact)}
              >
                Download
              </button>
              {state.imageCopyAvailable && (tab === 'photo' || tab === 'card') && (
                <button
                  class="secondary-action"
                  type="button"
                  onClick={() => void runtime.copyMediaImage(artifact)}
                >
                  Copy Image
                </button>
              )}
            </>
          )}
          {state.publicLinkAvailable && (
            <button
              class="secondary-action"
              type="button"
              onClick={() => void runtime.copyMediaRideLink()}
            >
              Copy Ride Link
            </button>
          )}
          <span class="media-overflow">
            <OverflowButton
              label="More media actions"
              size="compact"
              type="button"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen((open) => !open)}
            />
            {moreOpen && (
              <div class="bc-popover-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMoreOpen(false);
                    runtime.startReplay();
                  }}
                >
                  <Play aria-hidden="true" size={17} />
                  Watch Full Replay
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={busy}
                  onClick={() => {
                    setMoreOpen(false);
                    setConfirmDelete(true);
                  }}
                >
                  <Trash2 aria-hidden="true" size={17} />
                  Delete Local Media
                </button>
              </div>
            )}
          </span>
        </div>

        {confirmDelete && (
          <div class="media-delete-confirm" role="group" aria-label="Confirm media deletion">
            <strong>Delete local ride files?</strong>
            <span>
              This removes the replay and generated previews from this browser. It does not delete
              the coaster or downloaded files.
            </span>
            <button class="secondary-action" type="button" onClick={() => setConfirmDelete(false)}>
              Cancel
            </button>
            <button
              class="danger-action"
              type="button"
              onClick={() => void runtime.deleteRideMedia()}
            >
              Delete Local Media
            </button>
          </div>
        )}

        <Disclosure summary="Privacy & storage">
          <p class="media-privacy-note">
            Created on this device and never uploaded by Buncha Coasters. Generated videos and loops
            stay ready while this coaster remains open. Reaction Cam recordings are removed when you
            leave this coaster, reload, or close the tab.
            {!state.publicLinkAvailable && (
              <> Private links are never placed inside exported media.</>
            )}
          </p>
        </Disclosure>
      </section>
    </div>
  );
}
